import { Alert, Box, Card, CardContent, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { EmissionTier, MetricState, AdaptationView, CanonicalPeriod } from '../common'
import { usePropertyClimateData, isActiveMetricState } from '../../../hooks/climateData'
import { useCurrentUser } from '../../../hooks/users'
import { ExtremeWindScoreBadge } from '../common/extremeWind/utils'
import ExtremeWindSpeedTrends from './extremeWind/ExtremeWindSpeedTrends'
import ClimateMetricCard from '../common/ClimateMetricCard'
import ClimateComputationIndicator from '../common/ClimateComputationIndicator'
import MetricStatePlaceholder from '../common/MetricStatePlaceholder'
import { getMostRelevantMetricState, MetricValueFallback } from '../common/MetricStateIndicator'
import ClimateSectionTitle from '../common/ClimateSectionTitle'
import { renderHelpLines } from '../common/aggregateHelpText'
import {
  BI_DAYS_LABEL,
  DAMAGE_AAL_LABEL,
  FINANCIAL_BREAKDOWN_TITLE,
  formatCurrencyValue,
  formatPlainNumber,
  parseMetricPointY,
  REVENUE_AAL_LABEL,
  sumNumbers,
  TOTAL_AAL_LABEL,
} from '../common/financialMetrics'
import { extremeWindDashboardConfig, type ExtremeWindHazardCode } from '../common/extremeWind/dashboardConfig'

type PropertyExtremeWindProps = {
  propertyId: number
  emissionTier: EmissionTier
  selectedPeriod?: CanonicalPeriod
  view?: AdaptationView
  hazardCode?: ExtremeWindHazardCode
}

const PropertyExtremeWind: React.FC<PropertyExtremeWindProps> = ({
  propertyId,
  emissionTier,
  selectedPeriod = CanonicalPeriod.LONG_TERM,
  view,
  hazardCode = 'tc',
}) => {
  const dashboardConfig = extremeWindDashboardConfig[hazardCode]

  if (!propertyId) {
    return <Alert severity="info">Select a property to view {dashboardConfig.label} metrics.</Alert>
  }

  const queryParams = useMemo(() => ({ view }), [view])
  const {
    data: scoreData,
    isLoading: scoreLoading,
    error: scoreError,
  } = usePropertyClimateData(`${hazardCode}:score`, { propertyId }, queryParams)
  const {
    data: damageAalData,
    isLoading: damageAalLoading,
    error: damageAalError,
  } = usePropertyClimateData(`${hazardCode}:average_damage_loss`, { propertyId }, queryParams)
  const {
    data: revenueAalData,
    isLoading: revenueAalLoading,
    error: revenueAalError,
  } = usePropertyClimateData(`${hazardCode}:average_bi_loss`, { propertyId }, queryParams)
  const {
    data: biDaysData,
    isLoading: biDaysLoading,
    error: biDaysError,
  } = usePropertyClimateData(`${hazardCode}:average_bi_days`, { propertyId }, queryParams)

  const { currencySymbol } = useCurrentUser()

  const loading = scoreLoading || damageAalLoading || revenueAalLoading || biDaysLoading
  const allErrored = !!(scoreError && damageAalError && revenueAalError && biDaysError)
  const hasData = !!(scoreData || damageAalData || revenueAalData || biDaysData)
  const overviewStates = [scoreData, damageAalData, revenueAalData, biDaysData]
    .map(entry => entry?.state?.value)
    .filter((value): value is MetricState => Boolean(value))
  const overviewHasSucceeded = overviewStates.some(value => value === MetricState.SUCCEEDED)
  const overviewActive = overviewStates.some(isActiveMetricState)
  const overviewAllUnsucceeded =
    overviewStates.length > 0 && overviewStates.every(value => value !== MetricState.SUCCEEDED)
  const overviewTerminalState = overviewStates.includes(MetricState.FAILED) ? MetricState.FAILED : overviewStates[0]

  if (loading && !hasData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (allErrored) {
    return <Alert severity="error">Unable to load {dashboardConfig.label} metrics for this property.</Alert>
  }

  if (!overviewHasSucceeded && !loading) {
    if (overviewActive) {
      return <ClimateComputationIndicator variant="block" minHeight={160} />
    }
    if (overviewAllUnsucceeded) {
      return <MetricStatePlaceholder state={overviewTerminalState} minHeight={160} />
    }
    return <Alert severity="info">No {dashboardConfig.label} data is available for this property.</Alert>
  }

  const metricNode = (result: typeof scoreData) => {
    const tierNode = result?.data?.data?.[emissionTier]
    return dashboardConfig.periodized ? tierNode?.[selectedPeriod] : tierNode
  }
  const scoreValue = scoreData?.state?.value === MetricState.SUCCEEDED ? parseMetricPointY(metricNode(scoreData)) : null
  const damageAalValue =
    damageAalData?.state?.value === MetricState.SUCCEEDED ? parseMetricPointY(metricNode(damageAalData)) : null
  const revenueAalValue =
    revenueAalData?.state?.value === MetricState.SUCCEEDED ? parseMetricPointY(metricNode(revenueAalData)) : null
  const biDaysValue =
    biDaysData?.state?.value === MetricState.SUCCEEDED ? parseMetricPointY(metricNode(biDaysData)) : null

  const totalAalValue = sumNumbers([damageAalValue, revenueAalValue])
  const totalAalState = getMostRelevantMetricState([damageAalData?.state?.value, revenueAalData?.state?.value])
  const totalAalHelpText = renderHelpLines([
    `${TOTAL_AAL_LABEL} = Damage AAL + BI AAL.`,
    dashboardConfig.periodized
      ? 'Values reflect the selected climate horizon.'
      : 'Extreme wind uses a single long-term horizon.',
  ])
  const horizonDescription = dashboardConfig.periodized ? 'selected horizon' : 'long-term horizon'

  return (
    <Stack spacing={4}>
      <Card>
        <CardContent>
          <Box mb={3}>
            <ClimateSectionTitle
              title="Overview"
              subtitle={`Summary of ${horizonDescription} ${dashboardConfig.label} risk`}
              helpText={`${dashboardConfig.title} metrics use the ${horizonDescription} shown in the page header.`}
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <ClimateMetricCard
                title="Risk Rating"
                variant="outlined"
                helpText={`${dashboardConfig.title} risk rating for the ${horizonDescription}.`}>
                {scoreValue != null ? (
                  <ExtremeWindScoreBadge score={scoreValue} size="regular" />
                ) : (
                  <MetricValueFallback
                    state={scoreData?.state?.value}
                    fallback={`${dashboardConfig.title} risk rating is unavailable.`}
                  />
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ClimateMetricCard title={TOTAL_AAL_LABEL} variant="outlined" helpText={totalAalHelpText}>
                {totalAalValue != null ? (
                  <Typography variant="h3">{formatCurrencyValue(totalAalValue, currencySymbol, 0)}</Typography>
                ) : (
                  <MetricValueFallback state={totalAalState} fallback="Total AAL data is unavailable." />
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ClimateMetricCard
                title={BI_DAYS_LABEL}
                variant="outlined"
                helpText={`Expected business interruption days per year for the ${horizonDescription}.`}>
                {biDaysValue != null ? (
                  <Typography variant="h3">{`${formatPlainNumber(biDaysValue, 1)} days/year`}</Typography>
                ) : (
                  <MetricValueFallback
                    state={biDaysData?.state?.value}
                    fallback="Business interruption days are unavailable."
                  />
                )}
              </ClimateMetricCard>
            </Grid>
          </Grid>

          <Box mt={4}>
            <ClimateSectionTitle
              title={FINANCIAL_BREAKDOWN_TITLE}
              subtitle={`Damage and revenue components of annualised ${dashboardConfig.label} impacts`}
              helpText={`Financial metrics are annualised expected losses for the ${horizonDescription}.`}
            />
          </Box>

          <Grid container spacing={3} mt={0.5}>
            <Grid item xs={12} md={6}>
              <ClimateMetricCard
                title={DAMAGE_AAL_LABEL}
                variant="outlined"
                helpText={`Annualised structure damage loss for the ${horizonDescription}.`}>
                {damageAalValue != null ? (
                  <Typography variant="h3">{formatCurrencyValue(damageAalValue, currencySymbol, 0)}</Typography>
                ) : (
                  <MetricValueFallback state={damageAalData?.state?.value} fallback="Damage AAL is unavailable." />
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ClimateMetricCard
                title={REVENUE_AAL_LABEL}
                variant="outlined"
                helpText={`Annualised business interruption revenue loss for the ${horizonDescription}.`}>
                {revenueAalValue != null ? (
                  <Typography variant="h3">{formatCurrencyValue(revenueAalValue, currencySymbol, 0)}</Typography>
                ) : (
                  <MetricValueFallback state={revenueAalData?.state?.value} fallback="BI AAL is unavailable." />
                )}
              </ClimateMetricCard>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <ExtremeWindSpeedTrends
            propertyId={propertyId}
            emissionTier={emissionTier}
            selectedPeriod={selectedPeriod}
            view={view}
            hazardCode={hazardCode}
          />
        </CardContent>
      </Card>
    </Stack>
  )
}

export default PropertyExtremeWind
